<template>
    <div>
        <input type="text"
            class="form-control"
            v-model="textValue"
            @keydown="handleKeypress"
            @blur="handleBlur"
            @focus="handleFocus"
            v-bind="$attrs"
            ref="textInput"
            autocomplete="off"
            />
        <div style="display: none">
        <div ref="title" class="btn-toolbar" role="toolbar" aria-label="Toolbar with button groups">
            <div class="btn-group btn-group-sm flex-grow-1" role="group" aria-label="Month Selection">
                <button type="button" class="btn btn-default flex-grow-0" @click="goPrev"><font-awesome-icon :icon="['fal', 'chevron-double-left']" /></button>
                <button type="button" class="btn btn-default">{{month.toFormat('LLLL yyyy')}}</button>
                <button type="button" class="btn btn-default flex-grow-0" @click="goNext"><font-awesome-icon :icon="['fal', 'chevron-double-right']" /></button>
            </div>
        </div>
        <div class="date-picker small" ref="content" tabindex="0" @keydown="handleCalKeydown">
            <div class="cal-week cal-week-head">
                <span class="cal-head">Sun</span>
                <span class="cal-head">Mon</span>
                <span class="cal-head">Tue</span>
                <span class="cal-head">Wed</span>
                <span class="cal-head">Thu</span>
                <span class="cal-head">Fri</span>
                <span class="cal-head">Sat</span>
            </div>
            <div class="cal-week" v-for="(week,idx) of calendarWeeks(month)" :key="idx">
                <span v-for="(day, i2) of week" :key="i2"
                    class="cal-day text-center"
                    :class="{ 'text-muted': day.muted, 'cal-selected': day.selected }"
                    @click="() => selectDay(day.date)"
                    >
                    {{day.label}}
                </span>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import bootstrap from '@/../node_modules/bootstrap/dist/js/bootstrap';
import { DateTime } from 'luxon';

export default {
    name: 'AlzataDatePicker',
    props: {
        modelValue: [Date, String],
        format: {
            type: String,
            default: 'L/d/yyyy',
        },
    },
    emits: [
        'update:modelValue',
    ],
    data() {
        return {
            popover: null,
            textValue: '',
            month: DateTime.local().startOf('month'),
            date: null,
            preventHide: false,
        };
    },
    watch: {
        textValue(newVal) {
            var rval = this.tryParse(newVal);
            if (rval) {
                this.date = rval;
            }
        },
        date(newVal, oldVal) {
            this.$emit('update:modelValue', newVal.toJSDate());
            const newMonth = newVal.startOf('month');
            if (!oldVal || !newMonth.equals(oldVal.startOf('month'))) {
                this.month = newMonth;
            }
        },
        modelValue(newVal) {
            const date = this.tryParse(newVal);
            if (date) {
                this.textValue = date.toFormat(this.format);
            }
        },
    },
    mounted() {
        this.popover = bootstrap.Popover.getOrCreateInstance(this.$refs.textInput, {
            container: 'body',
            placement: 'bottom',
            html: true,
            content: this.$refs.content,
            title: this.$refs.title,
            customClass: 'date-picker-container',
            trigger: 'manual',
        });
    },
    methods: {
        tryParse(value) {
            if (!value) {
                return null;
            }
            if (value instanceof Date) {
                return DateTime.fromJSDate(value);
            }
            const formats = [
                this.format,
                'yyyy-LL-dd\'T\'HH:mm:ss',
                'L/d/yyyy',
                'L/d/yy',
                'L/d',
            ];
            for (var format of formats) {
                const tmp = DateTime.fromFormat(value, format);
                if (!tmp.invalid) {
                    return tmp;
                }
            }
            return null;
        },
        goPrev() {
            this.month = this.month.minus({months: 1});
        },
        goNext() {
            this.month = this.month.plus({months: 1});
        },
        selectDay(day) {
            this.date = day.startOf('day');
            this.textValue = this.date.toFormat(this.format);
            this.popover.hide();
        },
        handleKeypress(evt) {
            switch (evt.keyCode) {
                case 13:
                    this.selectDay(this.date);
                    break;
                case 40:
                    this.preventHide = true;
                    this.popover.show();
                    this.$refs.content.focus();
                    break;
            }
        },
        handleCalKeydown(evt) {
            const selectedDate = this.date || DateTime.local().startOf('day');
            switch (evt.keyCode) {
                case 13: // Enter
                    evt.preventDefault();
                    evt.stopPropagation();
                    this.selectDay(this.date);
                    this.$refs.textInput.focus();
                    break;
                case 33: // Page up
                    this.date = selectedDate.minus({month: 1});
                    this.textValue = this.date.toFormat(this.format);
                    break;
                case 34: // Page down
                    this.date = selectedDate.plus({month: 1});
                    this.textValue = this.date.toFormat(this.format);
                    break;
                case 35: // End
                    this.date = selectedDate.endOf(evt.ctrlKey ? 'month' : 'week');
                    this.textValue = this.date.toFormat(this.format);
                    break;
                case 36: // Home
                    this.date = selectedDate.startOf(evt.ctrlKey ? 'month' : 'week');
                    this.textValue = this.date.toFormat(this.format);
                    break;
                case 37: // Left
                    this.date = selectedDate.minus({day: 1});
                    this.textValue = this.date.toFormat(this.format);
                    break;
                case 38: // Up
                    this.date = selectedDate.minus({week: 1});
                    this.textValue = this.date.toFormat(this.format);
                    break;
                case 39: // Right
                    this.date = selectedDate.plus({day: 1});
                    this.textValue = this.date.toFormat(this.format);
                    break;
                case 40: // Down
                    this.date = selectedDate.plus({week: 1});
                    this.textValue = this.date.toFormat(this.format);
                    break;
                case 27: // Escape
                    this.$refs.textInput.focus();
                    break;
            }
        },
        handleFocus() {
            this.popover.show();
        },
        handleBlur() {
            setTimeout(() => {
                if (!this.popover.tip.contains(document.activeElement))
                {
                    this.popover.hide();
                }
            }, 0);
        },
        calendarWeeks(refDate) {
            const monthStart = (refDate || DateTime.local()).startOf('month');
            let day = monthStart.startOf('week').minus({days: 1});
            const rval = [];
            do {
                const week = [];
                for (var i = 0; i < 7; ++i) {
                    week.push({
                        date: day,
                        label: day.day,
                        selected: this.date ? day.startOf('day').equals(this.date.startOf('day')) : false,
                        muted: day.month !== monthStart.month,
                    });
                    day = day.plus({days: 1});
                }
                rval.push(week);
            }
            while (day.month === monthStart.month);
            return rval;
        },
    },
};
</script>